import {Component, Inject} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {FormsModule} from '@angular/forms';
import {
  DisplayConcept,
  DisplayField,
  DisplayTerm,
  FeedbackContext,
  FeedbackContextEntry,
  Language
} from '@twpub/core/models';

export interface FieldPickerData {
  sectionLabel: string;
  fields: DisplayField[];
  concept: DisplayConcept;
  term?: DisplayTerm;
  languages: Language[];
}

/** Fields excluded from the picker — IDs are always in context, dictionary is sent separately */
const EXCLUDED_OIDS = ['cid', 'id'];

@Component({
  selector: 'pub-feedback-field-picker-dialog',
  templateUrl: './feedback-field-picker-dialog.component.html',
  styleUrls: ['./feedback-field-picker-dialog.component.scss'],
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButton, MatFormField, MatLabel, MatSelect, MatOption, FormsModule]
})
export class FeedbackFieldPickerDialogComponent {
  selectableFields: DisplayField[];
  selectedIndex: number | null = null;

  // Localized labels
  conceptIdLabel = $localize`:@@baseResultView.conceptId:Concept ID`;
  languageLabel = $localize`:@@baseResultView.language:Language`;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FieldPickerData,
    private dialogRef: MatDialogRef<FeedbackFieldPickerDialogComponent>
  ) {
    const isTermSection = data.sectionLabel === 'term';
    const dictOid = data.concept?.dictionaryName?.oid;

    // Filter out IDs and dictionary (dictionary is sent separately to the backend)
    this.selectableFields = data.fields.filter(f =>
      !EXCLUDED_OIDS.includes(f.oid) && f.oid !== dictOid
    );

    // Add term name as selectable only for term-level sections
    if (isTermSection && data.term) {
      const termNameField: DisplayField = {
        oid: 'term-name',
        jsonField: 'term-name',
        label: 'Term',
        value: data.term.name
      };
      if (!this.selectableFields.some(f => f.oid === 'term-name' || (f.oid === 'term' && f.value === data.term?.name))) {
        this.selectableFields.unshift(termNameField);
      }
    }

    // Add "General" option first — for missing fields or general entry feedback
    const generalLabel = $localize`:@@feedback.picker.general:<No specific field / General feedback>`;
    this.selectableFields.unshift({oid: 'general', jsonField: 'general', label: generalLabel, value: ''});

    // Default to "General"
    this.selectedIndex = 0;
  }

  proceed(): void {
    if (this.selectedIndex === null) return;
    const selectedField = this.selectableFields[this.selectedIndex];

    const entries: FeedbackContextEntry[] = [];

    // Concept ID
    if (this.data.concept) {
      entries.push({label: this.conceptIdLabel, value: this.data.concept.cid});
    }

    // Term ID, Term name, Language — only for term-level sections
    if (this.data.sectionLabel === 'term' && this.data.term) {
      entries.push({label: 'Term ID', value: this.data.term.tid});
      entries.push({label: 'Term', value: this.data.term.name});

      const lang = this.data.languages?.find(l => l.code === this.data.term!.languageCode);
      let langStr = lang?.name || this.data.term.languageCode;
      if (this.data.term.regionCode) langStr += ` (${this.data.term.regionCode})`;
      if (this.data.term.scriptCode) langStr += ` [${this.data.term.scriptCode}]`;
      entries.push({label: this.languageLabel, value: langStr});
    }

    // User-selected field (skip "General" and duplicates)
    if (selectedField.oid !== 'general') {
      const alreadyIncluded = entries.some(e => e.label === selectedField.label && e.value === (selectedField.value ?? ''));
      if (!alreadyIncluded) {
        entries.push({label: selectedField.label, value: selectedField.value ?? ''});
      }
    }

    this.dialogRef.close({entries} as FeedbackContext);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
